import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Head from "next/head";
import { useRouter } from "next/router";
import debounce from "lodash/debounce";
import CatalogList from "@components/Features/catalog/catalog-list/catalog-list";
import {
    CATALOG_PAGE_ADBLOCK_POSITIONS,
    FilterParams,
    generateFilterObject,
    SourceToCountry
} from "@components/Features/catalog/catalog-utils";
import MapView from "@components/Features/catalog/map-view/map-view";
import MainFilter from "@components/Features/MainPage/MainFilter/MainFilter";
import useIsMobile from "@src/hooks/useIsMobile";
import { Aggregations } from "@src/interfaces/map-response.scheme";
import { String2StringObj } from "@src/utils/utils";
import { useGetAnalyticsForCategoryQuery } from "@app/store/analyticsApi";
import { clearFilters, setCatalogParamsSort, setCatalogTimeSort } from "@app/store/filtersSlice";
import { disableFooter, enableFooter } from "@app/store/layoutSlice";
import { useGetOffersQuery } from "@app/store/offersApi";
import { AppState } from "@app/store/store";
import { Pagination } from "@widgets/Pagination";
import {
    generateCatalogDescription,
    generateCatalogHeader1,
    generateCatalogTitle
} from "@features/seo";
import { generateCatalogUri } from "@entities/catalog";
import { configApi } from "@shared/lib/configApi";
import Car from "@shared/lib/interfaces/car.interface";
import { RouterConfig } from "@shared/lib/routerConfig";
import { getBreadcrumbCatalogSchema } from "@shared/lib/utils/getBreadcrumbCatalogSchema";
import { getCatalogCarSchema } from "@shared/lib/utils/getCatalogCarSchema";
import { CustomHead, DEFAULT_OG_IMAGE } from "@shared/ui/CustomHead";
import { SchemaJsonLd } from "@shared/ui/SchemaJsonLd";
import { CatalogScreenProps, DEFAULT_PRECISION } from "./constants";
import { getCreatedTime, getPrecision, sortValueToQueryParam } from "./lib";
import classes from "./CatalogScreen.module.scss";

const CatalogScreen = ({ preloadedProps }: CatalogScreenProps) => {
    const ssrOffers = preloadedProps?.preloadOffers || [];
    const ssrQuantity = preloadedProps?.preloadQuantity || 0;
    const ssrTotalPages = preloadedProps?.preloadTotalPages || 1;
    const ssrOgImage = preloadedProps?.preloadOffers?.[0]?.imagesList?.[0]?.big || null;

    const dispatch = useDispatch<any>();
    const router = useRouter();
    const currentPage = router.query.page ? parseInt(router.query.page as string) : 1;

    const [dataMemory, setDataMemory] = useState<Car[]>([]);
    const [previewQnt, setPreviewQnt] = useState<null | number>(ssrQuantity);
    const [totalPages, setTotalPages] = useState<number>(ssrTotalPages);
    const [mapListMode, setMapListMode] = useState(false);

    const minHeight = useSelector((state: AppState) => state.layout.minHeight);

    const {
        engineVolumeFrom: engineVolumeFromValue,
        engineVolumeTo: engineVolumeToValue,
        wheel: wheelValue,
        priceFrom: priceFromValue,
        priceTo: priceToValue,
        mileageFrom: mileageFromValue,
        mileageTo: mileageToValue,
        horsePowerFrom: horsePowerFromValue,
        horsePowerTo: horsePowerToValue,
        generation: generationValue,
        ownersQntFrom: ownerQntFrom,
        ownersQntTo,
        sourceId,
        country,
        drive: driveValue,
        engineType: engineTypeValue,
        transmission: transmissionValue,
        body: bodyValue,
        yearFrom: yearFromValue,
        yearTo: yearToValue,
        model: modelValue,
        brand: brandValue,
        city: citiesValue,
        sellerType,
        hideNoImages
    } = useSelector((state: AppState) => state.filters);

    const [updateFilters, setUpdateFilters] = React.useState<FilterParams>({});
    const [newBounds, setNewBounds] = useState<number[][]>();
    const [newZoom, setNewZoom] = useState<number>(DEFAULT_PRECISION);
    const formattedCoordinates = useMemo(() => {
        if (newBounds) {
            return {
                bottomLeft: `${newBounds[0][0]};${newBounds[0][1]}`,
                topRight: `${newBounds[1][0]};${newBounds[1][1]}`
            };
        }
        return { bottomLeft: undefined, topRight: undefined };
    }, [newBounds]);

    const catalogTimeSort = useSelector((state: AppState) => state.filters.catalogTimeSort);
    const catalogAllParamsSort = useSelector(
        (state: AppState) => state.filters.catalogAllParamsSort
    );

    const [createdTimePrev, setCreatedTimePrev] = useState<string | null>(null);
    useEffect(() => {
        setCreatedTimePrev(getCreatedTime(catalogTimeSort));
    }, [catalogTimeSort]);
    const createdParamSort = sortValueToQueryParam(catalogAllParamsSort);

    const isMobile = useIsMobile();

    const [filters, setFilters] = React.useState<FilterParams>(() => {
        const initialFilters = generateFilterObject({
            brandValue: brandValue?.id,
            modelValue: modelValue?.id,
            generationValue: generationValue?.id,
            yearFromValue,
            yearToValue,
            engineVolumeFromValue,
            engineVolumeToValue,
            bodyValue,
            transmissionValue,
            engineTypeValue,
            driveValue,
            wheelValue,
            priceFromValue,
            priceToValue,
            mileageFromValue,
            mileageToValue,
            horsePowerFromValue,
            horsePowerToValue,
            ownerQntFrom,
            ownersQntTo,
            citiesValue,
            sellerType,
            sourceId,
            country,
            hideNoImages
        });
        return {
            ...initialFilters,
            sort: sortValueToQueryParam(catalogAllParamsSort),
            created_at: getCreatedTime(catalogTimeSort)
        };
    });

    const [header1, setHeader1] = useState(
        generateCatalogHeader1({
            brand: brandValue?.name,
            model: modelValue?.name,
            generation: generationValue?.name,
            qnt: ssrQuantity,
            city: citiesValue[0]?.name
        })
    );

    const mapQuery = router.query?.mapView;
    const [mapMode, setMapMode] = useState(!!mapQuery);

    const handleDisableMapListMode = () => {
        if (!mapMode) {
            setMapListMode(false);
        }
    };

    const handleScrollToCatalog = () => {
        if (isMobile) {
            setTimeout(() => {
                document.getElementById("catalog-list")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
            }, 300);
        }
    };

    useEffect(() => {
        const mapQuery = router.query?.mapView;
        setMapMode(!!mapQuery);
    }, [router.query]);

    useEffect(() => {
        if (mapMode) {
            dispatch(disableFooter());
            setMapListMode(true);
        } else {
            dispatch(enableFooter());
        }

        return () => {
            dispatch(enableFooter());
        };
    }, [mapMode]);

    const {
        data: updatePreviewQntData,
        isLoading: updatePreviewQntIsLoading,
        isFetching: updatePreviewQntIsFetching
    } = useGetOffersQuery({
        ...updateFilters
    });

    useEffect(() => {
        const newFilters = generateFilterObject({
            brandValue: brandValue?.id,
            modelValue: modelValue?.id,
            generationValue: generationValue?.id,
            yearFromValue,
            yearToValue,
            engineVolumeFromValue,
            engineVolumeToValue,
            bodyValue,
            transmissionValue,
            engineTypeValue,
            driveValue,
            wheelValue,
            priceFromValue,
            priceToValue,
            mileageFromValue,
            mileageToValue,
            horsePowerFromValue,
            horsePowerToValue,
            ownerQntFrom,
            ownersQntTo,
            citiesValue,
            sellerType,
            sourceId,
            country,
            hideNoImages
        });

        setUpdateFilters({
            ...newFilters
        });
    }, [
        brandValue,
        modelValue,
        yearFromValue,
        yearToValue,
        bodyValue,
        transmissionValue,
        engineTypeValue,
        driveValue,
        wheelValue,
        priceFromValue,
        priceToValue,
        mileageFromValue,
        mileageToValue,
        horsePowerFromValue,
        horsePowerToValue,
        generationValue,
        ownerQntFrom,
        ownersQntTo,
        citiesValue,
        engineVolumeFromValue,
        engineVolumeToValue,
        sellerType,
        sourceId,
        country,
        hideNoImages
    ]);

    const [skip, setSkip] = useState(false);
    const { data, error, isLoading, isFetching } = useGetOffersQuery(
        {
            page: currentPage,
            ...filters,
            sort: createdParamSort,
            created_at: createdTimePrev,
            ...(mapListMode
                ? {
                      bottom_left: formattedCoordinates.bottomLeft,
                      top_right: formattedCoordinates.topRight,
                      precision: newZoom
                  }
                : {})
        },
        {
            skip: skip
        }
    );

    const debounceSetQnt = React.useRef(
        debounce(async (value) => {
            setPreviewQnt(value);
        }, 150)
    ).current;

    useEffect(() => {
        debounceSetQnt(updatePreviewQntData?.meta?.total);
    }, [updatePreviewQntData]);

    const [isPrevDataLoading, setIsPrevDataLoading] = useState(false);

    const debounceSetIsPreviewDataLoading = React.useRef(
        debounce(async (value) => {
            setIsPrevDataLoading(value);
        }, 150)
    ).current;

    useEffect(() => {
        const isPreviewDataFetching =
            updatePreviewQntIsFetching || updatePreviewQntIsLoading || isFetching || isLoading;
        debounceSetIsPreviewDataLoading(isPreviewDataFetching);
    }, [updatePreviewQntIsFetching, isFetching, isLoading, updatePreviewQntIsLoading]);

    useEffect(() => {
        const newSkip = mapMode && !newBounds;
        setSkip(newSkip);
    }, [isLoading, isFetching, mapMode, newBounds]);

    const [analyticsFilters, setAnalyticsFilters] = React.useState<String2StringObj>({});
    useEffect(() => {
        let newFilters: String2StringObj = {};

        const values = [
            brandValue,
            modelValue,
            yearFromValue,
            generationValue,
            bodyValue,
            transmissionValue,
            engineTypeValue,
            driveValue,
            engineVolumeFromValue
        ];

        if (values.some((val) => !val)) {
            setAnalyticsFilters({});
            return;
        }

        if (brandValue) {
            newFilters.mark = brandValue?.name;
        }

        if (modelValue) {
            newFilters.model = modelValue?.name;
        }

        if (yearFromValue) {
            newFilters.year = yearFromValue.toString();
        }

        if (generationValue && generationValue.generation) {
            newFilters.generation = generationValue.generation.toString();
        }

        if (engineVolumeFromValue) {
            newFilters.displacement = engineVolumeFromValue.toString();
        }

        if (bodyValue) {
            newFilters.prep_body_type = bodyValue;
        }

        if (engineTypeValue) {
            newFilters.prep_engine_type = engineTypeValue;
        }

        if (driveValue) {
            newFilters.prep_drive_type = driveValue;
        }

        if (transmissionValue) {
            newFilters.prep_transmission = transmissionValue;
        }

        setAnalyticsFilters(newFilters);
    }, [
        brandValue,
        modelValue,
        yearFromValue,
        generationValue,
        bodyValue,
        transmissionValue,
        engineTypeValue,
        driveValue,
        engineVolumeFromValue
    ]);
    const { data: analyticsData } = useGetAnalyticsForCategoryQuery(analyticsFilters, {});

    const reset = () => {
        dispatch(clearFilters());
        setSkip(false);
        router.push(RouterConfig.CATALOG);
    };

    useEffect(() => {
        if (data && data.offers) {
            setDataMemory(data.offers);
            setTotalPages(data.meta.totalPage);
        }

        if (currentPage === 1) {
            const sourceIdSingle = sourceId && sourceId.length === 1 ? sourceId[0] : undefined;
            const params = {
                brand: brandValue?.name,
                model: modelValue?.name,
                generation: generationValue?.name,
                qnt: data?.meta?.total || 0,
                city: citiesValue[0]?.name,
                country: sourceIdSingle ? SourceToCountry[sourceIdSingle] : undefined
            };
            const newH1 = generateCatalogHeader1(params);
            setHeader1(newH1);
        }
    }, [data, currentPage]);

    const applyFilters = async (skipPush = false, forceFilters: Record<string, any>) => {
        setSkip(true);
        setSkip(false);
        const newFilters = generateFilterObject({
            brandValue: brandValue?.id,
            modelValue: modelValue?.id,
            generationValue: generationValue?.id,
            yearFromValue,
            yearToValue,
            engineVolumeFromValue,
            engineVolumeToValue,
            bodyValue,
            transmissionValue,
            engineTypeValue,
            driveValue,
            wheelValue,
            priceFromValue,
            priceToValue,
            mileageFromValue,
            mileageToValue,
            horsePowerFromValue,
            horsePowerToValue,
            ownerQntFrom,
            ownersQntTo,
            citiesValue,
            sellerType,
            sourceId,
            country,
            hideNoImages,
            ...forceFilters
        });

        setFilters(newFilters);

        const catalogUri = generateCatalogUri(
            country,
            citiesValue,
            brandValue?.alias,
            modelValue?.alias,
            generationValue?.generation,
            generationValue?.restyleNumber,
            {
                bodyType: bodyValue,
                priceFrom: priceFromValue,
                priceTo: priceToValue
            }
        );

        const urlWithMapView = mapMode ? `${catalogUri}?mapView=true` : catalogUri;
        if (!skipPush) {
            await router.push(urlWithMapView, undefined, { shallow: true });
        }
    };

    const description = generateCatalogDescription({
        brand: brandValue?.name,
        model: modelValue?.name,
        generation: generationValue?.name,
        city: citiesValue[0]?.name,
        qnt: ssrQuantity,
        country
    });

    const titleTagContent = generateCatalogTitle({
        brand: brandValue?.name,
        model: modelValue?.name,
        generation: generationValue?.name,
        city: citiesValue[0]?.name,
        qnt: ssrQuantity,
        country
    });

    const keywords = [
        "авто",
        "auto",
        "продажа авто",
        "автомобили",
        "автосалоны",
        "авторынок",
        "базар авто",
        "каталог автомобилей",
        brandValue?.name,
        modelValue?.name,
        generationValue?.name
    ]
        .filter(Boolean)
        .join(", ");

    const shouldShowPagination = !isFetching && dataMemory.length > 0;

    const metaRobots = currentPage === 1 ? "index,follow" : "noindex,follow";
    const canonicalUrl = `${configApi.API_PUBLIC_DOMAIN}${router.asPath.split("?")[0]}`;
    const canonicalWithPage =
        currentPage === 1 ? canonicalUrl : `${canonicalUrl}?page=${currentPage}`;
    const linkRelPrev = currentPage > 1 ? `${canonicalUrl}?page=${currentPage - 1}` : null;
    const linkRelNext = currentPage < totalPages ? `${canonicalUrl}?page=${currentPage + 1}` : null;
    const breadcrumbSchema = getBreadcrumbCatalogSchema(brandValue, modelValue);

    const productsCarSchema = brandValue
        ? getCatalogCarSchema({
              canonicalUrl: canonicalWithPage,
              title: titleTagContent,
              description: description,
              brandName: brandValue?.name,
              offersData: ssrOffers,
              totalOffers: ssrQuantity
          })
        : null;

    const handleBoundsChange = (bounds: number[][], zoom = DEFAULT_PRECISION) => {
        const precision = getPrecision(zoom);
        setNewBounds(bounds);
        setNewZoom(precision);
    };

    const handleViewModeChange = (isMap: boolean) => {
        setMapMode(isMap);
    };

    return (
        <div
            style={{
                height: mapMode ? minHeight : "auto"
            }}
            className={classes["catalog-screen"]}
        >
            <CustomHead
                title={titleTagContent}
                description={description}
                keywords={keywords}
                canonical={canonicalWithPage}
                robots={metaRobots}
                openGraph={{
                    title: titleTagContent,
                    description: description,
                    type: "website",
                    image: {
                        url: ssrOgImage || DEFAULT_OG_IMAGE,
                        alt: titleTagContent
                    }
                }}
            />
            <Head>
                {linkRelPrev && <link rel="prev" href={linkRelPrev} />}
                {linkRelNext && <link rel="next" href={linkRelNext} />}
            </Head>
            <SchemaJsonLd
                schemas={[breadcrumbSchema, ...(productsCarSchema ? [productsCarSchema] : [])]}
            />
            <div className={classes["catalog-screen__inner"]}>
                <div className={classes["catalog-screen__border"]}>
                    <div className={classes["catalog-screen__filter"]}>
                        <MainFilter
                            previewQnt={previewQnt}
                            isPreviewQntFetching={isPrevDataLoading}
                            onFilterSubmit={async (forceFilters: Record<string, any>) => {
                                await applyFilters(false, forceFilters);
                                handleScrollToCatalog();
                            }}
                            resetFilters={reset}
                            isMainPage={false}
                            mapMode={mapMode}
                            disableMapListMode={handleDisableMapListMode}
                        />
                    </div>
                </div>

                {mapMode && (
                    <MapView
                        filters={filters}
                        onBoundsChanged={handleBoundsChange}
                        total={data?.meta?.total}
                        isDataLoading={isFetching || isLoading}
                        data={data?.aggregations?.geotile_grid as Aggregations[]}
                        setViewMode={handleViewModeChange}
                    />
                )}

                {!mapMode && (
                    <div className={classes["catalog-screen__error"]}>
                        {!!error && (
                            <div className={classes["catalog-screen__error-text"]}>
                                Ошибка загрузки данных
                            </div>
                        )}

                        {!error && (
                            <>
                                <CatalogList
                                    analyticsData={analyticsData}
                                    onParamsSortChanged={(sort) => {
                                        dispatch(setCatalogParamsSort(sort));
                                    }}
                                    onTimeSortChanged={(sort) => {
                                        dispatch(setCatalogTimeSort(sort));
                                    }}
                                    showLoadMore={false}
                                    timeSort={catalogTimeSort}
                                    allParamsSort={catalogAllParamsSort}
                                    data={dataMemory}
                                    isDataLoading={isFetching || isLoading}
                                    page={currentPage}
                                    loadMore={() => {}}
                                    forceSortShow={true}
                                    title={header1}
                                    total={data?.total}
                                    banners={data?.banners}
                                    adBlockPositions={CATALOG_PAGE_ADBLOCK_POSITIONS}
                                />
                                {shouldShowPagination && (
                                    <Pagination
                                        className={classes["catalog-screen__pagination"]}
                                        totalPages={totalPages}
                                        currentPage={currentPage}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
export default CatalogScreen;